import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useAuth } from '../../context/AuthContext';
import { FillPage } from '../../layout/FillPage';
import { useNavCounts } from '../../layout/useNavCounts';
import { formatPhone } from '../../shared/phone';
import { formatShortDateTime, relativeTime } from '../../shared/time';
import { useHeld, useLeave } from '../../shared/useLeave';
import type { VoicemailListItem } from '../../types/voicemail';
import {
  Avatar,
  Button,
  Callout,
  Checkbox,
  Composer,
  EmptyState,
  HueGlyph,
  IconArchive,
  IconCheck,
  IconCheckCircle,
  IconChevronDown,
  IconChevronLeft,
  IconDocument,
  IconEye,
  IconEyeOff,
  IconInbox,
  IconMessage,
  IconSparkle,
  IconVoicemail,
  InboxGroup,
  InboxListSkeleton,
  InboxRow,
  MessageThread,
  Reason,
  RefreshFailed,
  Segmented,
  Skeleton,
  Tag,
  TranscriptView,
  Workspace,
} from '../../ui/v2';
import { firstName, toThread } from '../SmsInbox/smsModel';
import {
  LIST_PAGE_SIZE,
  useVoicemail,
  useVoicemailActions,
  useVoicemailList,
} from './voicemailData';
import {
  BUCKET_LABEL,
  callerDisplayName,
  callerLabel,
  categoryLabel,
  categoryTone,
  type Folder,
  FOLDER_LABEL,
  folderFromParams,
  FOLDERS,
  groupByBucket,
} from './voicemailModel';

/**
 * Voicemail: the same master/detail as Texts. The open message reads
 * caller, then the existing AI summary, then the transcript. The reply
 * composer stays open on this page (VOICEMAIL_V2_SPEC.md).
 */

export const VOICEMAIL_BASE = '/voicemail';

export default function Voicemail() {
  const [params, setParams] = useSearchParams();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const folder = folderFromParams(params);
  const unreadOnly =
    folder === 'inbox' && ['true', '1'].includes(params.get('unread') ?? '');
  const [limit, setLimit] = useState(LIST_PAGE_SIZE);
  useEffect(() => setLimit(LIST_PAGE_SIZE), [folder, unreadOnly]);

  const list = useVoicemailList({ folder, unread: unreadOnly, limit });
  const unreadCount = useNavCounts({ voicemail: true }).voicemail;
  const listActions = useVoicemailActions();
  const [markingRead, setMarkingRead] = useState(false);

  const rows = useMemo(() => list.data?.rows ?? [], [list.data]);
  const groups = useMemo(() => groupByBucket(rows, folder), [rows, folder]);
  const selectedId = id && /^\d+$/.test(id) ? Number(id) : null;
  const selectedRow = rows.find((v) => v.id === selectedId) ?? null;
  const idx = rows.findIndex((v) => v.id === selectedId);

  const setParam = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([k, v]) =>
          v ? next.set(k, v) : next.delete(k),
        );
        return next;
      },
      { replace: true },
    );

  const open = (v: VoicemailListItem | null) => {
    const qs = params.toString();
    navigate(
      `${v ? `${VOICEMAIL_BASE}/${v.id}` : VOICEMAIL_BASE}${qs ? `?${qs}` : ''}`,
    );
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if ((e.key === 'ArrowDown' || e.key === 'j') && idx < rows.length - 1) {
        e.preventDefault();
        open(rows[idx + 1]);
      }
      if ((e.key === 'ArrowUp' || e.key === 'k') && idx > 0) {
        e.preventDefault();
        open(rows[idx - 1]);
      }
      if (e.key === 'Escape' && selectedId != null) open(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, rows, selectedId, params]);

  const hasMore = !!list.data && list.data.total > rows.length;

  return (
    <FillPage>
      <PageMeta title="AskArthur | Voicemail" description="Voicemail inbox" />
      <Workspace
        title="Voicemail"
        meta={unreadCount > 0 ? `${unreadCount} unread` : 'Inbox clear'}
        toolbar={
          <div className="flex items-center gap-4 px-4 py-2.5 lg:px-6 lg:py-3">
            <div className="flex min-w-0 items-center gap-4">
              <Segmented<Folder>
                aria-label="Folder"
                className="shrink-0"
                items={FOLDERS.map((f) => ({
                  key: f,
                  label: FOLDER_LABEL[f],
                  count:
                    f === 'inbox' && unreadCount > 0 ? unreadCount : undefined,
                }))}
                value={folder}
                onChange={(f) =>
                  setParam({ folder: f === 'inbox' ? null : f, unread: null })
                }
              />
              {folder === 'inbox' && (
                <Checkbox
                  label="Unread only"
                  checked={unreadOnly}
                  onChange={(e) =>
                    setParam({ unread: e.target.checked ? 'true' : null })
                  }
                  className="whitespace-nowrap"
                />
              )}
            </div>
            {unreadCount > 0 && (
              <Button
                size="sm"
                variant="secondary"
                leading={<IconCheck size={14} />}
                loading={markingRead}
                onClick={() => {
                  setMarkingRead(true);
                  void listActions
                    .markAllRead()
                    .catch(() => undefined)
                    .finally(() => setMarkingRead(false));
                }}
                className="ml-auto shrink-0"
              >
                Mark all read
              </Button>
            )}
          </div>
        }
      >
        <div className="flex min-h-0 flex-1">
          <div
            className={clsx(
              'flex min-h-0 w-full flex-col bg-v2-surface lg:w-[380px] lg:shrink-0 lg:border-r lg:border-v2-line',
              selectedId != null && 'hidden lg:flex',
            )}
          >
            {list.isRefetchError && list.data && (
              <RefreshFailed
                className="m-3"
                what="Voicemail"
                onRetry={() => void list.refetch()}
              />
            )}
            {list.isError && !list.data ? (
              <div className="p-4">
                <Callout tone="danger" title="Couldn’t load voicemails">
                  {list.error.message}
                  <div className="mt-2">
                    <Button size="sm" onClick={() => void list.refetch()}>
                      Retry
                    </Button>
                  </div>
                </Callout>
              </div>
            ) : list.isPending ? (
              <InboxListSkeleton label="Loading voicemails" />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={
                  folder === 'archived' ? <IconArchive /> : <IconCheckCircle />
                }
                title={
                  folder === 'archived'
                    ? 'No archived voicemails'
                    : unreadOnly
                      ? 'Nothing unread'
                      : 'Inbox clear'
                }
                description={
                  folder === 'archived'
                    ? 'Voicemails you archive move here.'
                    : 'When a caller leaves a message, Arthur transcribes it and it shows up here with a summary.'
                }
              />
            ) : (
              <div className="v2-scroll min-h-0 flex-1 overflow-y-auto">
                {groups.map((g) => (
                  <InboxGroup
                    key={g.bucket}
                    label={BUCKET_LABEL[g.bucket]}
                    count={g.items.length}
                  >
                    {g.items.map((v) => (
                      <VoicemailRow
                        key={v.id}
                        v={v}
                        selected={v.id === selectedId}
                        onOpen={() => open(v)}
                      />
                    ))}
                  </InboxGroup>
                ))}
                {hasMore && (
                  <div className="flex justify-center p-3">
                    <Button
                      size="sm"
                      loading={list.isFetching}
                      onClick={() => setLimit((n) => n + LIST_PAGE_SIZE)}
                    >
                      Load more
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className={clsx(
              'flex min-h-0 min-w-0 flex-1 flex-col bg-v2-surface',
              selectedId == null && 'hidden lg:flex',
            )}
          >
            {selectedId != null ? (
              <Detail
                key={selectedId}
                id={selectedId}
                row={selectedRow}
                folder={folder}
                onBack={() => open(null)}
              />
            ) : (
              <div className="v2-fade-in">
                <EmptyState
                  icon={<IconVoicemail />}
                  title="Select a voicemail"
                  description="Pick a message on the left, or press ↓ to open the first one."
                />
              </div>
            )}
          </div>
        </div>
      </Workspace>
    </FillPage>
  );
}

const VoicemailRow: React.FC<{
  v: VoicemailListItem;
  selected: boolean;
  onOpen: () => void;
}> = ({ v, selected, onOpen }) => {
  const tone = categoryTone(v.matched_category);
  const name = callerDisplayName(v);
  return (
    <InboxRow
      selected={selected}
      unread={!v.is_read}
      onOpen={onOpen}
      avatar={
        <Avatar
          name={name || undefined}
          identity={v.caller_phone}
          hue="auto"
          size="md"
        />
      }
      title={callerLabel(v)}
      anonymous={!name}
      badges={v.is_vip && <Tag tone="warning">VIP</Tag>}
      at={v.call_time}
      atLabel={relativeTime(v.call_time)}
      preview={v.ai_summary || v.reason || 'Summary unavailable'}
      trailing={
        tone && (
          <Tag tone={tone} className="max-w-[45%] shrink-0 truncate">
            {categoryLabel(v.matched_category)}
          </Tag>
        )
      }
    />
  );
};

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

/**
 * One voicemail. Archiving moves it out of the inbox, so the panel slides
 * out and the list is left selected on nothing.
 */
const Detail: React.FC<{
  id: number;
  row: VoicemailListItem | null;
  folder: Folder;
  onBack: () => void;
}> = ({ id, row: liveRow, folder, onBack }) => {
  const detail = useVoicemail(id);
  const actions = useVoicemailActions();
  const { user } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  // One auto-read per time this voicemail is opened, so Mark unread sticks
  // until the detail is closed and opened again. Deep links use the detail
  // record when the row is not in the loaded list.
  const openedUnread = useRef<number | null>(null);
  const { leaving, leave } = useLeave(onBack);
  // While the panel leaves, the row drops out of the list and the record
  // flips to archived; keep showing what was acted on.
  const v = useHeld(detail.data, leaving);
  const row = useHeld(liveRow, leaving);
  const head = v ?? row;
  const thread = useMemo(
    () =>
      toThread(
        v?.sms_conversation?.messages ?? [],
        v ? callerDisplayName(v) : undefined,
      ),
    [v],
  );

  useEffect(() => {
    const current = v ?? row;
    if (!current || current.is_read) return;
    if (openedUnread.current === id) return;
    openedUnread.current = id;
    actions.update(id, { read: true }).catch(() => undefined);
  }, [id, v, row, actions]);

  if (detail.isError && !detail.data) {
    return (
      <div className="p-6">
        <Callout tone="danger" title="Couldn’t load this voicemail">
          {detail.error.message}
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => void detail.refetch()}>
              Retry
            </Button>
            <Button size="sm" variant="ghost" onClick={onBack}>
              Back to Voicemail
            </Button>
          </div>
        </Callout>
      </div>
    );
  }

  if (!head) return <DetailSkeleton />;

  const run = async (
    name: string,
    payload: { read?: boolean; archived?: boolean },
  ) => {
    setBusy(name);
    setError(null);
    // Archiving leaves the inbox. Restoring keeps the panel: the user may
    // be about to text back.
    const leaves = payload.archived === true && folder === 'inbox';
    const action = () => actions.update(id, payload);
    try {
      await (leaves ? leave(action) : action());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendError(null);
    try {
      await actions.reply(
        id,
        body,
        user ? { id: user.id, email: user.email } : null,
      );
      setDraft('');
    } catch (e) {
      setSendError((e as Error).message);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const tone = categoryTone(head.matched_category);
  const displayName = callerDisplayName(head);
  const guestFirst = firstName(displayName);
  const transcriptPending = v?.transcript_status === 'pending';
  const transcriptBody = v?.transcript?.trim();
  // Until the detail arrives, keep the control so the summary isn't the only
  // thing on screen. Once we know there is nothing to disclose, omit it.
  const showTranscript = !v || transcriptPending || !!transcriptBody;

  return (
    <section
      aria-label={`Voicemail from ${callerLabel(head)}`}
      className={clsx(
        'flex min-h-0 flex-1 flex-col',
        leaving && 'v2-slide-out-right',
      )}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-v2-line px-4 py-3.5 lg:px-6">
        <Button
          size="sm"
          variant="ghost"
          leading={<IconChevronLeft size={14} />}
          onClick={onBack}
          className="-ml-2 lg:hidden"
        >
          Voicemail
        </Button>
        <Avatar
          name={displayName || undefined}
          identity={head.caller_phone}
          hue="auto"
          size="lg"
          className="hidden lg:inline-flex"
        />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h2
              className={clsx(
                'truncate text-v2-18 font-bold tracking-[-0.015em]',
                !displayName && 'v2-num',
              )}
            >
              {callerLabel(head)}
            </h2>
            {head.is_vip && <Tag tone="warning">VIP</Tag>}
            {tone && (
              <Tag tone={tone}>{categoryLabel(head.matched_category)}</Tag>
            )}
          </div>
          <p className="v2-num mt-0.5 flex min-w-0 items-center gap-1.5 text-v2-12 text-v2-fg-3">
            <IconVoicemail size={13} className="shrink-0 text-v2-rose" />
            <span className="truncate">
              <time dateTime={head.call_time}>
                {formatShortDateTime(head.call_time)}
              </time>
              {displayName ? ` · ${formatPhone(head.caller_phone)}` : ''}
            </span>
            {head.caller_email && (
              <>
                <span aria-hidden>·</span>
                <a
                  href={`mailto:${head.caller_email}`}
                  className="truncate text-v2-fg-2 underline-offset-2 hover:underline"
                >
                  {head.caller_email}
                </a>
              </>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            leading={
              head.is_read ? <IconEyeOff size={14} /> : <IconEye size={14} />
            }
            loading={busy === 'read'}
            onClick={() => void run('read', { read: !head.is_read })}
          >
            <span className="max-sm:sr-only">
              {head.is_read ? 'Mark unread' : 'Mark read'}
            </span>
          </Button>
          {head.is_archived ? (
            <Button
              size="sm"
              variant="secondary"
              leading={<IconInbox size={14} />}
              loading={busy === 'archive'}
              onClick={() => void run('archive', { archived: false })}
            >
              <span className="max-sm:sr-only">Restore</span>
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              leading={<IconArchive size={14} />}
              loading={busy === 'archive'}
              onClick={() => void run('archive', { archived: true })}
            >
              <span className="max-sm:sr-only">Archive</span>
            </Button>
          )}
        </div>
      </header>

      <div className="v2-scroll min-h-0 flex-1 overflow-y-auto">
        <div className="max-w-[720px] px-4 py-5 lg:px-8 lg:py-6">
          {error && (
            <Callout
              tone="danger"
              className="mb-5"
              title="That didn’t go through"
            >
              {error}
            </Callout>
          )}

          {head.status === 'no_match' && !head.ai_summary ? (
            <Callout tone="neutral" title="No message left">
              {head.reason || 'The caller hung up before leaving a message.'}
            </Callout>
          ) : (
            <section
              aria-labelledby="voicemail-summary"
              className="rounded-v2-lg bg-v2-accent/[0.07] px-4 py-3.5"
            >
              <h3
                id="voicemail-summary"
                className="flex items-center gap-2 text-v2-12 font-semibold text-v2-accent"
              >
                <HueGlyph hue="accent" size="xs">
                  <IconSparkle size={12} />
                </HueGlyph>
                AI summary
              </h3>
              <p className="mt-2 max-w-[68ch] text-v2-15 leading-[22px] text-v2-fg">
                {head.ai_summary || head.reason || 'Summary unavailable.'}
              </p>
            </section>
          )}

          {showTranscript && (
            <div className="mt-3">
              <TranscriptToggle
                expanded={transcriptOpen}
                controls="voicemail-transcript"
                onClick={() => setTranscriptOpen((open) => !open)}
              >
                {transcriptOpen
                  ? 'Hide full transcript'
                  : 'View full transcript'}
              </TranscriptToggle>
              {transcriptOpen && (
                <div
                  id="voicemail-transcript"
                  role="region"
                  aria-label="Full transcript"
                  className="v2-fade-in mt-2"
                >
                  {!v ? (
                    <Skeleton className="h-3" width="60%" />
                  ) : transcriptPending ? (
                    <Reason>
                      Transcript pending — Arthur is still processing this call.
                    </Reason>
                  ) : (
                    <TranscriptView
                      transcript={v.transcript}
                      guestLabel={guestFirst || 'Caller'}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {v && (
            <div className="mt-5 border-t border-v2-line pt-5">
              <h3 className="flex items-center gap-2 text-v2-13 font-semibold text-v2-fg">
                <HueGlyph hue="accent" size="xs">
                  <IconMessage size={12} />
                </HueGlyph>
                Text {guestFirst || 'the caller'} back
                {v.sms_enabled && thread.length > 0 && (
                  <span className="v2-num font-medium text-v2-fg-3">
                    {thread.length} {thread.length === 1 ? 'text' : 'texts'}
                  </span>
                )}
              </h3>
              <div className="-mx-4 mt-3 lg:-mx-8">
                {v.sms_enabled ? (
                  <>
                    <MessageThread
                      messages={thread}
                      guestName={callerDisplayName(v) || null}
                      guestIdentity={v.caller_phone}
                      className="max-h-[420px] border-t border-v2-line"
                      empty={
                        <EmptyState
                          compact
                          icon={<IconMessage />}
                          title="No texts with this caller yet"
                          description="Your reply goes out as a text from the store’s number and continues in Texts."
                        />
                      }
                    />
                    <Composer
                      value={draft}
                      onChange={setDraft}
                      onSend={() => void send()}
                      sending={sending}
                      error={sendError}
                      onDismissError={() => setSendError(null)}
                      placeholder={`Text ${guestFirst || 'the caller'} back…`}
                      aside={
                        v.sms_conversation
                          ? 'The conversation continues in Texts.'
                          : 'Starts a conversation in Texts.'
                      }
                    />
                  </>
                ) : (
                  <div className="px-4 lg:px-8">
                    <Reason>
                      Texting isn’t turned on for this store, so replies can’t
                      be sent from here.
                    </Reason>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/** Full-width disclosure row under the summary. */
const TranscriptToggle: React.FC<{
  expanded: boolean;
  controls: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ expanded, controls, onClick, children }) => (
  <button
    type="button"
    aria-expanded={expanded}
    aria-controls={controls}
    onClick={onClick}
    className="-mx-2 flex h-9 w-[calc(100%+16px)] items-center gap-2 rounded-v2 px-2 text-left text-v2-13 font-semibold text-v2-fg-2 transition-colors hover:bg-v2-sunken/70 hover:text-v2-fg"
  >
    <IconDocument size={14} className="shrink-0 text-v2-fg-3" />
    <span className="min-w-0 flex-1">{children}</span>
    <IconChevronDown
      size={14}
      className={clsx(
        'shrink-0 text-v2-fg-3 transition-transform duration-150',
        expanded && 'rotate-180',
      )}
    />
  </button>
);

const DetailSkeleton: React.FC = () => (
  <div aria-label="Loading voicemail" className="px-4 py-5 lg:px-6">
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded-full" />
      <Skeleton className="h-4" width="30%" />
    </div>
    <Skeleton className="mt-8 h-3" width="70%" />
    <Skeleton className="mt-2 h-3" width="55%" />
  </div>
);
